import Layout from "../components/Layout";
import ScrollReveal from "../components/ScrollReveal";
import { Link } from "react-router-dom";
import { ArrowRight, Home, Building2, Hammer, Wrench, ClipboardList, Layers } from "lucide-react";

const services = [
  {
    icon: Home,
    title: "Residential Construction",
    description: "From single-floor homes to multi-storey apartment complexes, we build residential spaces with precision and comfort in mind.",
    features: ["Apartments & Flats", "Housing Societies", "Bungalows & Villas"],
  },
  {
    icon: Building2,
    title: "Commercial Buildings",
    description: "Modern commercial spaces designed for productivity and aesthetics — offices, retail outlets, and mixed-use developments.",
    features: ["Office Complexes", "Retail Spaces", "Mixed-Use Projects"],
  },
  {
    icon: Hammer,
    title: "Renovations & Interiors",
    description: "Transform your existing property with our comprehensive renovation services, from structural changes to complete interior makeovers.",
    features: ["Full Renovations", "Interior Fit-outs", "Structural Modifications"],
  },
  {
    icon: Wrench,
    title: "Civil Works",
    description: "Expert civil engineering works ensuring structural stability, code compliance, and long-term durability.",
    features: ["Foundation Work", "RCC Structures", "Civil Infrastructure"],
  },
  {
    icon: ClipboardList,
    title: "Project Management",
    description: "End-to-end project management ensuring timely delivery, budget control, and quality at every phase.",
    features: ["Timeline Management", "Budget Oversight", "Quality Inspections"],
  },
  {
    icon: Layers,
    title: "Land Development",
    description: "Site preparation, land surveying, and development services to get your project ready for construction.",
    features: ["Site Preparation", "Land Surveying", "Statutory Approvals"],
  },
];

export default function Services() {
  return (
    <Layout>
      <section
        className="relative py-20 md:py-32 text-white bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: "url('https://images.unsplash.com/photo-1632201147654-f6f54427e538?w=1600&h=600&fit=crop')" }}
      >
        <div className="absolute inset-0 bg-primary/82" />
        <div className="container mx-auto px-4 relative z-10">
          <ScrollReveal>
            <div className="max-w-3xl">
              <h1 className="text-5xl md:text-6xl font-bold mb-6">Our Services</h1>
              <p className="text-xl text-gray-200">Comprehensive construction solutions from ground-up builds to complete renovations.</p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <ScrollReveal key={index} delay={index * 0.08}>
                  <div
                    data-testid={`service-card-${index}`}
                    className="bg-white p-8 rounded-xl shadow-sm border border-border hover:shadow-lg transition-all duration-300 group"
                  >
                    <div className="w-14 h-14 bg-secondary/20 rounded-lg flex items-center justify-center mb-5 group-hover:bg-secondary/30 transition-colors">
                      <Icon size={28} className="text-secondary" />
                    </div>
                    <h3 className="text-xl font-bold text-primary mb-3">{service.title}</h3>
                    <p className="text-muted-foreground leading-relaxed mb-4">{service.description}</p>
                    <ul className="space-y-1">
                      {service.features.map((f, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                          <span className="w-1.5 h-1.5 rounded-full bg-secondary flex-shrink-0" />
                          {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-primary text-white py-20">
        <div className="container mx-auto px-4">
          <ScrollReveal>
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="text-4xl font-bold mb-4">Ready to Start Your Project?</h2>
              <p className="text-gray-200 text-lg mb-8">Get in touch with us today for a free consultation and detailed quote.</p>
              <Link to="/contact" data-testid="services-cta-btn" className="inline-flex items-center gap-2 px-8 py-4 bg-secondary text-secondary-foreground font-bold rounded-lg hover:opacity-90 transition-opacity text-lg">
                Contact Us <ArrowRight size={20} />
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </Layout>
  );
}
